import React from "react";
import { motion } from "framer-motion";
import { Printer } from "lucide-react";
import { gbp, pct } from "../lib/iht";

const riskMeta = {
  low: { label: "Low likelihood of IHT", bg: "bg-secondary" },
  medium: { label: "Medium likelihood of IHT", bg: "bg-accent" },
  high: { label: "High likelihood of IHT", bg: "bg-destructive text-white" },
};

const BigStat = ({ label, value, testid, big }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-wider text-foreground/60">{label}</p>
    <p
      data-testid={testid}
      className={`font-mono font-bold leading-none mt-1 ${big ? "text-4xl sm:text-5xl" : "text-2xl"}`}
    >
      {value}
    </p>
  </div>
);

export const ResultsPanel = ({ r }) => {
  const risk = riskMeta[r.risk];
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-2xl font-bold">Your estimate</h2>
        <span
          data-testid="risk-band"
          className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 border-foreground ${risk.bg} whitespace-nowrap`}
        >
          {risk.label}
        </span>
      </div>

      <motion.div
        key={Math.round(r.ihtDue)}
        initial={{ scale: 0.96, opacity: 0.6 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="rounded-2xl border-2 border-foreground bg-white p-6 brutal-shadow-sm"
      >
        <BigStat label="Estimated IHT due" value={gbp(r.ihtDue)} testid="total-tax-result" big />
        <div className="grid grid-cols-2 gap-4 mt-5">
          <BigStat label="Effective rate" value={pct(r.effectiveRate)} testid="effective-rate-result" />
          <BigStat label="Net to heirs" value={gbp(r.netToHeirs)} testid="net-to-heirs-result" />
        </div>
      </motion.div>

      {/* Comical artwork placeholder — swap this for your own art */}
      <p data-testid="summary-text" className="text-sm font-medium leading-relaxed">
        {r.summary}
      </p>

      {r.isLargeEstate && (
        <p className="text-xs font-bold bg-white border-2 border-foreground rounded-lg p-3">
          Very large or complex estates may need professional advice.
        </p>
      )}

      <button
        type="button"
        data-testid="print-pdf-btn"
        onClick={() => window.print()}
        className="flex items-center justify-center gap-2 h-12 rounded-xl border-2 border-foreground bg-white font-bold brutal-shadow-sm hover:-translate-y-0.5 hover:-translate-x-0.5 hover:shadow-[6px_6px_0px_#1e2b24] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none transition-[transform,box-shadow] duration-150"
      >
        <Printer size={18} strokeWidth={2.5} /> Save / Print PDF
      </button>
    </div>
  );
};
